import React, {useEffect, useState} from 'react'
import ClientSuspense from '@components/ClientSuspense'
import SafeSuspense from '@components/SafeSuspense'
import SuspenseLoading from '@components/SuspenseLoading/Loading'
import {pageComponents} from '@data/fragments/website/__generated__/pageComponents'
import {getCategoriesAndProductsCPGsWeb_cached_categories} from '@data/queries/categories/__generated__/getCategoriesAndProductsCPGsWeb_cached'
import {getCategoriesAndProductsWeb_cached_categories} from '@data/queries/categories/__generated__/getCategoriesAndProductsWeb_cached'
import {
  getWebsiteMenuInformation_cached_design,
  getWebsiteMenuInformation_cached_website
} from '@data/queries/website/__generated__/getWebsiteMenuInformation_cached'
import {EVENTS, sendEvent} from '@helpers/events'
import useQuery from '@hooks/useMyOrderPreferencesWithDefault'
import useSessionStorageState from '@hooks/useSessionStorageState'
import Container from '@packages/justo-parts/lib/components/Container'
import dynamic from 'next/dynamic'
import {useRouter} from 'next/router'

import Loading from './Products/ProductsContent/Loading'
import {CartLoadingProvider} from './CartLoadingContext'
import Track from './Track'
import useLayout from './useLayout'
import useProductsLayout from './useProductsLayout'

import styles from './styles.module.css'

const OnTableModal = dynamic(() => import('./OnTableModal'), {ssr: false})
const PageMeta = dynamic(() => import('./PageMeta'), {ssr: false})
const Page = dynamic(() => import('@page-components/Page'), {ssr: false})
const Cart = dynamic(() => import('./Cart'))
const CartMenuV2 = dynamic(() => import('./CartMenuV2'))
const CategoriesLayout = dynamic(() => import('./CategoriesLayout'))
const NextOpenDate = dynamic(() => import('./NextOpenDate'))
const NoMenu = dynamic(() => import('./NoMenu'))
const Options = dynamic(() => import('./Options'))
const Products = dynamic(() => import('./Products'))
const CreateAddress = dynamic(() => import('@components/Address'), {ssr: false})

export interface OrderProps {
  website: getWebsiteMenuInformation_cached_website
  design: getWebsiteMenuInformation_cached_design
  categories?:
    | getCategoriesAndProductsWeb_cached_categories
    | getCategoriesAndProductsCPGsWeb_cached_categories
  productId?: string
  page?: pageComponents
  componentIdToSsrProps?: {[componentId: string]: any}
}

export default function Order(props: OrderProps) {
  const {website, design, productId, categories, page, componentIdToSsrProps} = props

  const router = useRouter()
  const {preferences, loading} = useQuery(website)
  const layout = useLayout(website)
  const [onTable] = useSessionStorageState('onTable')
  const productsLayout = useProductsLayout(website)

  const {addressModal} = router.query
  const [modalOpen, setModalOpen] = useState<boolean>(!!addressModal)

  useEffect(() => {
    sendEvent(EVENTS.pageView.order, {})
  }, [])

  if (!preferences || loading) {
    return (
      <div className={styles.loadingContainer}>
        <Container>
          <Loading />
        </Container>
      </div>
    )
  }

  const menuId = preferences.menuId
  if (!menuId) return <NoMenu />
  if (!website.deliveryTypes) return null
  if (!website.deliveryTypes.length) return null

  const nextOpenDate = <NextOpenDate deliverAt={preferences.deliverAt} store={preferences.store} />

  const productCardDesign = design?.design.productCardDesign

  const products =
    productsLayout === 'categories' ? (
      <CategoriesLayout
        website={website}
        menuId={menuId}
        preferences={preferences}
        productCardDesign={productCardDesign}
        storeId={preferences?.store?._id}
        deliverAt={preferences?.deliverAt}
      />
    ) : (
      <Products
        website={website}
        productsLayout={productsLayout}
        layout={layout}
        menuId={menuId}
        loading={loading}
        nextOpenDate={nextOpenDate}
        preferences={preferences}
        productCardDesign={productCardDesign}
        preloadedCategories={categories}
        storeId={preferences?.store?._id}
        deliverAt={preferences?.deliverAt}
      />
    )


  return (
    <SafeSuspense fallback={<SuspenseLoading />}>
      <CartLoadingProvider>
        <div className={styles.container}>
          {modalOpen && <CreateAddress closeModal={() => setModalOpen(false)} />}
          {onTable ? <OnTableModal /> : null}
          {page ? (
            <Page pageId={page._id} page={page} componentIdToSsrProps={componentIdToSsrProps} />
          ) : null}
          {layout === 'mobile' ? (
            <>
              <Cart website={website} preferences={preferences} layout={layout} />
              <div className={styles.mobileMarginTop} />
            </>
          ) : null}
          {layout === 'v2' ? (
            <CartMenuV2
              loading={loading}
              website={website}
              preferences={preferences}
              layout={layout}
            />
          ) : null}
          <Container>
            {preferences.store && layout !== 'v2' ? nextOpenDate : null}
            {layout === 'mobile' ? (
              <div>
                <Options preferences={preferences} website={website} layout={layout} />
                <ClientSuspense fallback={<SuspenseLoading />}>{products}</ClientSuspense>
              </div>
            ) : null}
            {layout === 'v1' ? (
              <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-4 last-sm">
                  <Options preferences={preferences} website={website} layout={layout} />
                  {layout === 'mobile' ? null : (
                    <Cart website={website} preferences={preferences} layout={layout} />
                  )}
                </div>
                <div className="col-xs-12 col-sm-6 col-md-8">{products}</div>
              </div>
            ) : null}
          </Container>
          {layout === 'v2' ? <div>{products}</div> : null}
          {productId ? null : <PageMeta website={website} />}
          <Track />
        </div>
      </CartLoadingProvider>
    </SafeSuspense>
  )
}
